import { ChangeDetectionStrategy, Component, inject, input, output, TemplateRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { NgTemplateOutlet } from '@angular/common';
import { TranslocoDirective } from '@jsverse/transloco';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AraDialogHeaderComponent } from '../ara-dialog-header/ara-dialog-header.component';
import { AraButtonColor, AraButtonComponent } from '../ara-button/ara-button.component';

const DEFAULT_MIN_WIDTH = 'min(30rem, 80vw)';

export interface AraGenericDialogAction {
	labelKey: string;
	icon?: string;
	/** Omit for the neutral default; `primary` marks the one affirmative action, `error` a destructive one. */
	color?: AraButtonColor;
	disabled?: () => boolean;
	/** Shows the in-button spinner while an async action runs (e.g. save) and blocks further clicks. */
	loading?: () => boolean;
	/** Optional transloco key for a tooltip shown on hover (also visible while disabled). */
	tooltipKey?: string;
	handler: (ref: MatDialogRef<AraGenericDialogComponent>) => void;
}

export interface AraGenericDialogData {
	titleKey: string;
	contentTemplate: TemplateRef<unknown>;
	contentContext?: Record<string, unknown>;
	/** Footer actions. Omit (or pass an empty array) to render a dialog without a button bar. */
	actions?: AraGenericDialogAction[];
	/** Overrides the responsive default min-width (`min(30rem, 80vw)`). */
	minWidth?: string;
	/** Overrides the default 85vh cap. The actual width/height are set from outside via MatDialogConfig. */
	maxHeight?: string;
}

@Component({
	selector: 'ara-generic-dialog',
	standalone: true,
	imports: [
		AraDialogHeaderComponent,
		MatDialogContent,
		MatDialogActions,
		NgTemplateOutlet,
		TranslocoDirective,
		AraButtonComponent,
		MatIconModule,
		MatTooltipModule,
	],
	templateUrl: './ara-generic-dialog.component.html',
	styleUrl: './ara-generic-dialog.component.scss',
	// Eager (= immer prüfen), damit in Projection-Mode action.disabled() bei Tastendruck im Host-Formular
	// neu ausgewertet wird. Mit dem v22-Default (Richtung OnPush) bliebe der Save-Button stale/disabled.
	changeDetection: ChangeDetectionStrategy.Eager,
	host: {
		// Fill-Modus (opt-in): schaltet die :host-Höhen- und mat-dialog-content-Regeln im SCSS scharf.
		'[class.ara-generic-dialog--fill]': 'fillHeight()',
	},
})
export class AraGenericDialogComponent {
	/**
	 * Two usage modes:
	 * - Service mode: opened via {@link AraGenericDialogService}; title/content/actions come from MAT_DIALOG_DATA.
	 * - Projection mode: used as `<ara-generic-dialog [titleKey] [actions] (closed)>` inside another dialog
	 *   component, with the body provided via content projection. Inputs take precedence over MAT_DIALOG_DATA.
	 */
	private readonly dialogRef = inject<MatDialogRef<AraGenericDialogComponent>>(MatDialogRef);
	private readonly data = inject<AraGenericDialogData | undefined>(MAT_DIALOG_DATA, { optional: true });

	readonly titleKey = input<string>();
	readonly title = input<string>();
	readonly subtitle = input<string>();
	readonly actions = input<AraGenericDialogAction[]>();
	/** Overrides the responsive default min-width (`min(30rem, 80vw)`). */
	readonly minWidth = input<string>();
	/** Caps the dialog height (default 85vh). The real height comes from MatDialogConfig (`dialog.open({ height })`). */
	readonly maxHeight = input<string>();
	/**
	 * Opt-in fill mode (projection mode). Set when the dialog is opened with a fixed `height`
	 * (`dialog.open({ height })`) and the projected body must STRETCH to that height instead of
	 * collapsing to its content height — e.g. an iframe or canvas. Off by default, so every
	 * content-sized dialog is unaffected. See the styles for what the flag enables.
	 *
	 * REQUIREMENT for nested projection: if this dialog sits inside another component (an
	 * intermediate host between the CDK dialog surface and this element, e.g.
	 * `ara-innovation-round-dialog`), that host MUST set `:host { display: block; height: 100%; }`
	 * in its own styles — otherwise the height chain breaks and the body will not fill (empty
	 * space returns). No action needed when this component is opened directly (service mode).
	 */
	readonly fillHeight = input<boolean>(false);
	/** Fires when the header close-X is clicked OR the backdrop (area outside the dialog) is clicked (projection mode). */
	readonly closed = output<void>();

	constructor() {
		this.dialogRef
			.backdropClick()
			.pipe(takeUntilDestroyed())
			.subscribe(() => {
				if (!this.dialogRef.disableClose) {
					this.close();
				}
			});
	}

	protected get effectiveTitleKey(): string {
		return this.titleKey() ?? this.data?.titleKey ?? '';
	}

	protected get effectiveMinWidth(): string {
		return this.minWidth() ?? this.data?.minWidth ?? DEFAULT_MIN_WIDTH;
	}

	protected get effectiveMaxHeight(): string {
		return this.maxHeight() ?? this.data?.maxHeight ?? '85vh';
	}

	protected get effectiveActions(): AraGenericDialogAction[] {
		return this.actions() ?? this.data?.actions ?? [];
	}

	/** Service mode renders this template; projection mode falls back to the projected `<ng-content>`. */
	protected get contentTemplate(): TemplateRef<unknown> | undefined {
		return this.data?.contentTemplate;
	}

	protected get contentContext(): Record<string, unknown> {
		return this.data?.contentContext ?? {};
	}

	protected close(): void {
		this.closed.emit();
		this.dialogRef.close();
	}

	/** Same predicate helpers as `ara-actions-menu-component`, so both action renderers match. */
	protected isDisabled(action: AraGenericDialogAction): boolean {
		return action.disabled?.() ?? false;
	}

	protected isLoading(action: AraGenericDialogAction): boolean {
		return action.loading?.() ?? false;
	}

	protected invoke(action: AraGenericDialogAction): void {
		action.handler(this.dialogRef);
	}
}
